import React, { useEffect, useState } from 'react';
import { Select, Card, Spin, Button, Popconfirm } from 'antd';
import { mapIpToMachineName } from 'containers/Performance/EnochPerf/common';
import EnochConfigForm from './EnochConfigForm';
import {
  ENOCH_DEFAULT_CONFIG_NAME,
  ENOCH_MACHINE_STATE,
} from './enoch-config.helpers';
import { useGetEnochAllMachineIps, useGetEnochConfig } from 'query/enoch';
import useEnochConfigForm, {
  enochConfigDefaultValues,
} from './useEnochConfigForm';
import { FormProvider } from 'react-hook-form';
import { cloneDeep, merge } from 'lodash';
import {
  useRunEnochJob,
  useSaveEnochConfig,
  useTerminateEnochJob,
} from 'mutation/enoch';
import './enoch-config.css';
import { useEnochState } from './useEnochState';
import RunEnoch from './RunEnoch';

const EnochConfig = () => {
  useEnochState();

  const {
    data: allEnochVMs,
    isFetching: isLoadingEnochVMs,
  } = useGetEnochAllMachineIps();

  const { mutate, isLoading: isSavingEnochConfig } = useSaveEnochConfig();

  const { mutate: terminateJob } = useTerminateEnochJob();

  const { mutate: runJob } = useRunEnochJob();

  const [selectedVM, setSelectedVM] = useState(ENOCH_DEFAULT_CONFIG_NAME);

  const {
    data: enochConfig,
    isFetching: isLoadingEnochConfig,
  } = useGetEnochConfig(selectedVM);

  const form = useEnochConfigForm();

  useEffect(() => {
    form.reset(
      merge(cloneDeep(enochConfigDefaultValues), cloneDeep(enochConfig || {})),
    );
  }, [selectedVM, isLoadingEnochConfig, enochConfig]);

  const handleSubmit = () => {
    const { useDefaultConfig, userDataDir, ...config } = form.getValues();
    mutate({
      ipAddress: selectedVM,
      config: {
        ...config,
        sendCountConfig: {
          unlimited: config.sendCountConfig?.count > 0 ? 0 : 1,
          count: config.sendCountConfig?.count,
        },
      },
      useDefaultConfig: !!useDefaultConfig,
      userDataDir: userDataDir?.trim(),
    });
  };

  return (
    <FormProvider {...form}>
      <Card className="enoch-card-container">
        <RunEnoch />

        <Spin spinning={isLoadingEnochVMs}>
          <div className="enoch-config-header">
            <Select id="enoch-vm" value={selectedVM} onChange={setSelectedVM}>
              <Select.Option value={ENOCH_DEFAULT_CONFIG_NAME}>
                DEFAULT CONFIGURATION
              </Select.Option>
              {allEnochVMs.map(vm => (
                <Select.Option key={vm.ipAddress} value={vm.ipAddress}>
                  {mapIpToMachineName[vm.ipAddress] || vm.ipAddress}
                </Select.Option>
              ))}
            </Select>

            {selectedVM && form.formState.isDirty && (
              <Popconfirm
                cancelText="No"
                okText="Yes"
                title="Are you sure to save this configuration?"
                onConfirm={handleSubmit}
              >
                <Button size="large" type="primary">
                  Save Config
                </Button>
              </Popconfirm>
            )}

            {selectedVM && !form.formState.isDirty && (
              <div>
                {(enochConfig?.state === ENOCH_MACHINE_STATE.RUNNING ||
                  enochConfig?.state === ENOCH_MACHINE_STATE.SCHEDULED) && (
                  <Popconfirm
                    cancelText="No"
                    okText="Yes"
                    title="Confirm To Terminate Job?"
                    onConfirm={() => terminateJob({ ipAddress: selectedVM })}
                  >
                    <Button size="large" type="primary">
                      Terminate Automation
                    </Button>
                  </Popconfirm>
                )}

                {enochConfig?.state === ENOCH_MACHINE_STATE.IDLE && (
                  <Popconfirm
                    cancelText="No"
                    okText="Yes"
                    title="Confirm To Run Job?"
                    onConfirm={() => runJob({ ipAddress: selectedVM })}
                  >
                    <Button size="large" type="primary">
                      Start Automation
                    </Button>
                  </Popconfirm>
                )}
              </div>
            )}
          </div>

          {!!selectedVM && (
            <Spin
              spinning={
                // prevent 2 spinners from showing up
                (isLoadingEnochConfig || isSavingEnochConfig) &&
                !isLoadingEnochVMs
              }
            >
              <EnochConfigForm
                machineState={enochConfig?.state}
                vm={selectedVM}
              />
            </Spin>
          )}
        </Spin>
      </Card>
    </FormProvider>
  );
};

export default EnochConfig;
